import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useVisibilityChange, useDebounce } from "@uidotdev/usehooks";
import { useGridApiRef } from '@mui/x-data-grid';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Input,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Toolbar,
} from '@mui/material';
import {
  AssessmentOutlined as DailyReportIcon,
  CalendarViewMonthOutlined as ScheduleIcon,
  Checklist as JobsIcon,
  FileDownloadOutlined as DownloadIcon,
  MoreVert as MoreVertIcon,
  Pause as PauseIcon,
  PhoneAndroid as DeviceIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  TrackChanges as TrackersIcon,
} from '@mui/icons-material';

import {
  AppDataGrid,
  APP_DATA_GRID_COLUMN_PRESETS,
  AppDataGridColumnSettingsButton,
  toast,
  Page,
  JobStatusFilter,
  useColumnVisibility,
} from 'components';
import { callApi, download, downloadTrafficCurveCsv, formatCompactDateRange, sanitizeDownloadBasename } from 'utils';
import { useSession, useMisc } from 'store';
import { t } from 'i18n';
import { DeviceCheck } from 'views/JobList/components';
import { FirstDayOrderFilter, GroupSchedule } from './components';

const statusColor = {
  running: 'statusRunning',
  active: 'statusActive',
  inactive: 'statusInactive',
  warning: 'statusWarning',
};

// enum for all action types
const ActionTypes = Object.freeze({
  PAUSE_JOBS: 1,
  RESUME_JOBS: 2,
  DELETE_JOBS: 3,
  SHOW_SCHEDULES: 4,
  SHOW_MOREOPS: 5,
  CLEANUP: 6,
  SHOW_DEVICE: 7,
  SHOW_DEVICE_PICKER: 8,
  SHOW_DEVICE_LOADING: 9,
});

const setFormatOrg = (misc) => (row, col) => misc.org[row[col]];
const formatDate = (row, _col) => {
  const fullRange = row.start_date + ' ~ ' + row.end_date;
  return (
    <Tooltip title={fullRange}>
      <span>{formatCompactDateRange(row.start_date, row.end_date)}</span>
    </Tooltip>
  );
};
const formatType = (row, col) => t(row[col]);
const setFormatName = (onDeviceClick) => (row, col) => {
  const name = row[col] || '';
  const openDeviceCheck = (ev) => {
    ev.stopPropagation();
    onDeviceClick(row);
  };

  return (
    <Box
      component="span"
      sx={{
        boxSizing: 'border-box',
        display: 'block',
        minWidth: 0,
        position: 'relative',
        width: '100%',
      }}
    >
    <Tooltip title={name}>
      <Box
        component="span"
        sx={{
          display: 'block',
          minWidth: 0,
          overflow: 'hidden',
          pr: row.has_prepared_dids ? 3.5 : 0,
          textAlign: 'center',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Box>
    </Tooltip>
    {row.has_prepared_dids && (
    <Tooltip title={t('Review Device Id')}>
      <IconButton
        aria-label={t('Review Device Id')}
        onClick={openDeviceCheck}
        size="small"
        sx={{
          color: 'primary.main',
          height: 24,
          p: 0,
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 24,
          zIndex: 1,
        }}
      >
        <DeviceIcon sx={{fontSize: 16}} />
      </IconButton>
    </Tooltip>
    )}
    </Box>
  );
};
const downloadOrderTrafficCurve = (row, trafficCurveName) => {
  callApi(`/jobs/${row.gid}/traffic_curve`).then(res => {
    if (!Array.isArray(res?.traffic_curve) || res.traffic_curve.length !== 24) {
      toast('没有可下载的曲线数据', 'warning');
      return;
    }
    downloadTrafficCurveCsv(res.traffic_curve, `traffic-curve-${row.gid}-${trafficCurveName || row.traffic_curve}`);
  }).catch(console.error);
};
const setFormatTraffic = (misc) => (row, col) => {
  const trafficCurve = row[col];
  if (!trafficCurve) {
    return '';
  }
  const trafficCurveName = misc.traffic_curve[trafficCurve] || trafficCurve;
  return (
    <Box component="span" sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0, width: '100%'}}>
      <Tooltip title={trafficCurveName}>
        <Box
          component="span"
          sx={{
            display: 'block',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {trafficCurveName}
        </Box>
      </Tooltip>
      <Tooltip title="下载曲线 CSV">
        <IconButton
          aria-label={`下载订单 ${row.gid} 流量曲线`}
          onClick={(ev) => {
            ev.stopPropagation();
            downloadOrderTrafficCurve(row, trafficCurveName);
          }}
          size="small"
          sx={{ml: 0.5, p: 0.25}}
        >
          <DownloadIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
const alertMessages = (alerts = []) => {
  const seen = new Set();
  return alerts.reduce((messages, alert) => {
    const message = alert?.message || alert?.type;
    if (!message || seen.has(message))
      return messages;

    seen.add(message);
    messages.push(message);
    return messages;
  }, []);
};
const alertTitle = (messages) => (
  <>
    {messages.map(message => (<div key={message}>{message}</div>))}
  </>
);
const formatStatus = (row, col) => {
  const messages = alertMessages(row.alerts);
  const node = (
    <Box
      component="span"
      sx={{
        color: theme => theme.vars.palette.business[statusColor[row[col]]],
        fontSize: '1.5em',
        fontWeight: 'bold',
      }}
    >
      ⦾
    </Box>
  );
  return messages.length ? (<Tooltip title={alertTitle(messages)}>{node}</Tooltip>) : node;
};
const setFormatAction = (setAction) => (row, _col) => [
  <Tooltip title={t('Pause Order')} key="pause">
    <IconButton
      onClick={() => setAction([ActionTypes.PAUSE_JOBS, row])}
      style={{display: row.cnt_all === row.cnt_suspend ? 'none' : undefined}}
      size="small"
      aria-label={t('Pause Order')}
    >
      <PauseIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>,
  <Tooltip title={t('Resume Order')} key="resume">
    <IconButton
      onClick={() => setAction([ActionTypes.RESUME_JOBS, row])}
      style={{display: row.cnt_suspend === 0 ? 'none' : undefined}}
      size="small"
      aria-label={t('Resume Order')}
    >
      <PlayIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>,
  <Tooltip title={t('Order Schedule')} key="schedule">
    <IconButton
      onClick={() => setAction([ActionTypes.SHOW_SCHEDULES, row])}
      size="small"
      aria-label={t('Order Schedule')}
    >
      <ScheduleIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>,
  <Tooltip title={t('Order Jobs')} key="list">
    <IconButton
      component={NavLink}
      to={`/jobs?gid=${row.gid}`}
      size="small"
      aria-label={t('Order Jobs')}
    >
      <JobsIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>,
  <Tooltip title={t('Order Daily Report')} key="dailyreport">
    <IconButton
      component={NavLink}
      to={`/reports/jobdaily?idtype=parent&id=${row.gid}`}
      size="small"
      aria-label={t('Order Daily Report')}
    >
      <DailyReportIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>,
  <Tooltip title={t('Order Track Stats')} key="trackstats">
    <IconButton
      component={NavLink}
      to={`/reports/trackers?idtype=parent&id=${row.gid}`}
      size="small"
      aria-label={t('Order Track Stats')}
    >
      <TrackersIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>,
  <IconButton onClick={(ev) => setAction([ActionTypes.SHOW_MOREOPS, row, ev.currentTarget])} size="small" key="moreops">
    <MoreVertIcon fontSize="inherit" />
  </IconButton>
];

const columns = [
  { name: 'gid', label: t('Group ID'), hideable: false },
  { name: 'name', label: t('Group Name'), hideable: false },
  { name: 'type', label: t('Type'), format: formatType },
  { name: 'org', label: t('Org') },
  { name: 'date', label: t('Start/End Date'), format: formatDate },
  { name: 'traffic_curve', label: t('Traffic') },
  { name: 'cnt_all', label: t('Jobs') },
  { name: 'total_imps', label: t('Total Imps') },
  { name: 'total_clicks', label: t('Total Clicks') },
  { name: 'today_imps', label: t('Today Imps') },
  { name: 'today_clicks', label: t('Today Clicks') },
  { name: 'today_actual_imps', label: t('Today Actual Imps') },
  { name: 'today_actual_clicks', label: t('Today Actual Clicks') },
  { name: 'status', label: t('Status'), format: formatStatus, hideable: false },
  { name: 'action', label: t('Action'), hideable: false },
];
const groupListColumnVisibilityMigration = {
  id: 'show-total-metrics-20260629',
  fields: ['total_imps', 'total_clicks'],
};

const dataGridColumnDefaults = {
  align: 'center',
  headerAlign: 'center',
  sortable: false,
  minWidth: 64,
  flex: 1,
};
const numericColumn = {
  align: 'right',
  headerAlign: 'right',
};

const columnSizing = {
  gid: {minWidth: 64, flex: 0.5},
  name: {...APP_DATA_GRID_COLUMN_PRESETS.primaryText, minWidth: 180, flex: 2.2, align: 'center', headerAlign: 'center', cellClassName: 'GroupList-nameCell'},
  type: {minWidth: 56, flex: 0.45},
  org: {minWidth: 76, flex: 0.8},
  date: {minWidth: 112, flex: 0.75},
  traffic_curve: {minWidth: 109, flex: 0.72},
  cnt_all: {...numericColumn, minWidth: 64, flex: 0.55},
  total_imps: {...numericColumn, minWidth: 76, flex: 0.75},
  total_clicks: {...numericColumn, minWidth: 72, flex: 0.7},
  today_imps: {...numericColumn, minWidth: 72, flex: 0.7},
  today_clicks: {...numericColumn, minWidth: 72, flex: 0.7},
  today_actual_imps: {...numericColumn, minWidth: 92, flex: 0.85},
  today_actual_clicks: {...numericColumn, minWidth: 92, flex: 0.85},
  status: {minWidth: 52, flex: 0.45},
  action: {minWidth: 188, flex: 0.8, sortable: false, cellClassName: 'AppDataGrid-actionCell'},
};

const toDataGridColumn = (col) => ({
  ...dataGridColumnDefaults,
  ...columnSizing[col.name],
  field: col.name,
  headerName: col.label,
  hideable: col.hideable !== false,
  renderCell: (params) => col.format ? col.format(params.row, col.name) : params.value,
});

function loopUpdateStatus(active, cb, delay, params) {
  if (!active)
    return;

  const query = params ? `?${params}` : '';
  const getAbnGroups = () => callApi(`/jobs/abngroups${query}`).then(res => {
    const entries = Object.entries(res || {});
    cb(entries.length ? Object.fromEntries(entries.map(([gid, alerts]) => [gid, {status: 'warning', alerts}])): null);
  }).catch(console.error);
  getAbnGroups();

  const itvl = setInterval(() => getAbnGroups(), delay);
  return () => clearInterval(itvl);
}

const defaultFilter = {status: 'active', first_day: '', org: '', q: ''};
const maxDownloadBasenameLength = 80;
const getOrderJobsDownloadName = (grp) => {
  const gid = grp?.gid ? String(grp.gid) : '';
  const suffixLength = gid ? gid.length + 1 : 0;
  const maxNameLength = Math.max(1, maxDownloadBasenameLength - suffixLength);
  const name = sanitizeDownloadBasename(grp?.name || '').slice(0, maxNameLength).replace(/-$/g, '');
  return gid ? `${name || 'jobs'}-${gid}` : (name || 'jobs');
};

const GroupList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const documentVisible = useVisibilityChange();
  const session = useSession((state) => state.session);
  const misc = useMisc((state) => state.data);
  const [cols, setCols] = useState([]);
  const [action, setAction] = useState(null);
  const [amend, setAmend] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const groupGridApiRef = useGridApiRef();
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState(null);
  const deviceRequestRef = useRef(0);

  const filter = Object.fromEntries(
    ['status', 'first_day', 'org', 'q'].map(k => [k, searchParams.get(k) || defaultFilter[k]])
  );

  const q = useDebounce(filter.q, 1500);
  const usp = Object.entries({...filter, q})
    .reduce((pv, [k, v]) => {v && pv.set(k, v); return pv}, new URLSearchParams())
    .toString();
  const dataUrl = `/jobs/groups?${usp}`;

  const isAdmin = session.role === 'admin';

  const updateSearchParam = (k, v) => {
    setSearchParams((prev) => {
      v !== defaultFilter[k] ? prev.set(k, v) : prev.delete(k);
      k !== 'page' && prev.delete('page');
      prev.delete('limit');
      return prev;
    });
  };

  const clearAction = useCallback(() => {
    deviceRequestRef.current += 1;
    setAction(null);
  }, []);
  const handleCloseMoreOps = useCallback(() => setTimeout(
    () => setAction(prev => prev?.[0] === ActionTypes.SHOW_MOREOPS ? null : prev),
    50
  ), []);
  const openDeviceCheck = useCallback((grp) => {
    const requestId = deviceRequestRef.current + 1;
    deviceRequestRef.current = requestId;
    setAction([ActionTypes.SHOW_DEVICE_LOADING, {group: grp, requestId}]);
    callApi(`/groups/${grp.gid}/prepareddids`).then((res) => {
      if (deviceRequestRef.current !== requestId) {
        return;
      }
      const rows = Array.isArray(res) ? res : (res?.rows || []);
      if (rows.length === 1) {
        setAction([ActionTypes.SHOW_DEVICE, rows[0]]);
      } else if (rows.length > 1) {
        setAction([ActionTypes.SHOW_DEVICE_PICKER, {group: grp, rows}]);
      } else {
        setAction(null);
        toast(t('No Custom Device Ids'), 'warning');
      }
    }).catch(ex => {
      console.error(ex);
      if (deviceRequestRef.current === requestId) {
        setAction(null);
      }
    });
  }, []);

  useEffect(() => void(setCols(
    columns.filter(col => isAdmin || col.name !== 'org').map(col => {
      let format;
      if (col.name === 'org')           format = setFormatOrg(misc);
      if (col.name === 'name')          format = setFormatName(openDeviceCheck);
      if (col.name === 'traffic_curve') format = setFormatTraffic(misc);
      if (col.name === 'action')        format = setFormatAction(setAction);
      return format ? {...col, format} : col;
    })
  )), [isAdmin, misc, openDeviceCheck]);
  const gridColumns = useMemo(() => cols.map(toDataGridColumn), [cols]);
  const visibilityColumns = useMemo(() => (
    cols.map(col => ({
      field: col.name,
      hideable: col.hideable !== false,
      defaultHidden: col.defaultHidden === true,
    }))
  ), [cols]);
  const {
    columnVisibilityModel,
    onColumnVisibilityModelChange,
  } = useColumnVisibility({
    columns: visibilityColumns,
    tableId: 'group-list',
    userId: session.uid,
    showOnceMigration: groupListColumnVisibilityMigration,
  });
  const columnSettingsSlotProps = useMemo(() => (
    columnSettingsAnchorEl ? {
      panel: {
        anchorEl: columnSettingsAnchorEl,
        placement: 'bottom-end',
      },
    } : undefined
  ), [columnSettingsAnchorEl]);

  const activeUpdate = documentVisible;
  useEffect(() => loopUpdateStatus(activeUpdate, setAmend, 60_000, usp), [activeUpdate, usp]);

  const reloadJobs = useCallback(() => setRefresh(prev => prev-1), []);

  let modalType, modalData, dialogContent, moreopsGrp, moreOpsEl;
  if (action) {
    const type = action[0];
    if (type === ActionTypes.PAUSE_JOBS || type === ActionTypes.RESUME_JOBS) {
      const grp = action[1];
      const txt = type === ActionTypes.PAUSE_JOBS
        ? `暂停订单 ${grp.name}?`
        : `继续订单 ${grp.name}?`;
      dialogContent = (<DialogContentText>{txt}</DialogContentText>);
    } else if (type === ActionTypes.CLEANUP) {
      const grp = action[1];
      if (grp) {
        dialogContent = (<DialogContentText>{`清理订单 ${grp.gid}: ${grp.name}?`}</DialogContentText>);
      }
    } else if (type === ActionTypes.SHOW_MOREOPS) {
      moreopsGrp = action[1];
      moreOpsEl = action[2];
    } else {
      modalType = type;
      modalData = action[1];
    }
  }

  const confirmDialog = () => {
    const [type, grp] = action;
    if (type === ActionTypes.PAUSE_JOBS || type === ActionTypes.RESUME_JOBS) {
      const data = {suspend: type === ActionTypes.PAUSE_JOBS};
      const url = `/jobs/batch/update?gid=${grp.gid}`;
      callApi(url, 'PUT', data).then(() => {
        setAction(null);
        reloadJobs();
      }).catch(console.error);
    } else if (type === ActionTypes.CLEANUP && grp) {
      const ids = grp.fc_id;
      callApi(`/jobs/cleanup?ids=${ids}`).then((res) => {
        console.log(res);
        setAction(null);
        toast(JSON.stringify(res), 'success');
      }).catch(console.error);
    }
  };

  return (
    <Page title={t('Job Groups')}>
      <Toolbar sx={{mt: -1, gap: 1, flexWrap: 'wrap'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, flexWrap: 'wrap'}}>
          <JobStatusFilter value={filter.status} onChange={(status) => updateSearchParam('status', status)} />
          <FirstDayOrderFilter
            value={filter.first_day}
            onChange={(firstDay) => updateSearchParam('first_day', firstDay)}
          />
        {isAdmin && (
          <Select
            name="org"
            value={filter.org}
            size="small"
            onChange={(ev) => updateSearchParam('org', ev.target.value)}
            displayEmpty
            sx={{ minWidth: 120, '& .MuiSelect-select': {py: '6px'} }}
          >
            <MenuItem value={''}>{t('All Orgs')}</MenuItem>
          {Object.entries(misc.org || {}).map(([val, display]) => (
            <MenuItem value={val} key={val}>{display}</MenuItem>
          ))}
          </Select>
        )}
        </Box>
        <Input
          sx={{p: '2px', mr: 1, width: 160}}
          name="q"
          value={filter.q}
          size="small"
          onChange={(ev) => updateSearchParam('q', ev.target.value)}
          inputProps={{ style: {marginLeft: '-5px'} }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />
        <Button color="inherit" onClick={reloadJobs}>{t('Reload')}</Button>
        <AppDataGridColumnSettingsButton
          apiRef={groupGridApiRef}
          buttonId="group-list-column-settings-button"
          panelId="group-list-column-settings-panel"
          label={t('Column Settings')}
          ref={setColumnSettingsAnchorEl}
        />
      </Toolbar>
      <Paper sx={{p: 1, pb: 0, m: 2, mt: -1, position: 'relative'}}>
        <AppDataGrid
          apiRef={groupGridApiRef}
          columns={gridColumns}
          idColumn="gid"
          dataUrl={dataUrl}
          dataAmend={amend}
          refresh={refresh}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          showColumnSettings
          showColumnSettingsToolbar={false}
          slotProps={columnSettingsSlotProps}
          sx={{
            '& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell.GroupList-nameCell': {
              overflow: 'visible',
            },
          }}
        />
      </Paper>
      <Menu
        id="jobops-menu"
        anchorEl={moreOpsEl}
        open={Boolean(moreopsGrp)}
        onClose={handleCloseMoreOps}
        onClick={handleCloseMoreOps}
      >
        <MenuItem component={NavLink} to={`/reports/jobregion?idtype=parent&id=${moreopsGrp?.gid}`}>{t('Region Report')}</MenuItem>
        <MenuItem onClick={() => download(`/jobs/export?gid=${moreopsGrp?.gid}`, getOrderJobsDownloadName(moreopsGrp))}>{t('Export Order Jobs')}</MenuItem>
        {['ctr', 'dayparts', 'overrun'].map(type => (
          <MenuItem key={type} onClick={() => download(`/jobs/export?gid=${moreopsGrp?.gid}&type=${type}`, `${getOrderJobsDownloadName(moreopsGrp)}-${type}`)}>
            {t({ctr: 'Export Order CTR', dayparts: 'Export Order Dayparts', overrun: 'Export Order Overrun'}[type])}
          </MenuItem>
        ))}
        <MenuItem component={NavLink} to={`/jobs/devusage?groupid=${moreopsGrp?.gid}`}>{t('FreqCtrl Stats')}</MenuItem>
        {misc.hasIpuaPvCount?.includes(moreopsGrp?.gid) && (
        <MenuItem component={NavLink} to={`/jobs/ipuauv?id=${moreopsGrp.gid}`}>{t('Daily UV Count')}</MenuItem>
        )}
        {isAdmin && moreopsGrp?.gid === moreopsGrp?.fc_id && (
        <MenuItem onClick={() => setAction([ActionTypes.CLEANUP, moreopsGrp])}>{t('Cleanup')}</MenuItem>
        )}
      </Menu>
      <GroupSchedule group={modalType === ActionTypes.SHOW_SCHEDULES ? modalData : null} onClose={clearAction} />
      <DeviceCheck job={modalType === ActionTypes.SHOW_DEVICE ? modalData : null} onClose={clearAction} />
      <Dialog
        open={modalType === ActionTypes.SHOW_DEVICE_LOADING}
        onClose={clearAction}
        aria-labelledby="prepared-dids-loading-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="prepared-dids-loading-title">
          {modalData?.group?.name ? `${t('Review Device Id')} - ${modalData.group.name}` : t('Review Device Id')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, py: 2}}>
            <CircularProgress size={24} />
            <span>{t('Loading')}</span>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearAction}>{t('Cancel')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={modalType === ActionTypes.SHOW_DEVICE_PICKER}
        onClose={clearAction}
        aria-labelledby="prepared-dids-picker-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="prepared-dids-picker-title">
          {modalData?.group?.name ? `${t('Review Device Id')} - ${modalData.group.name}` : t('Review Device Id')}
        </DialogTitle>
        <DialogContent>
          <List dense>
          {(modalData?.rows || []).map(row => (
            <ListItemButton key={row.fc_id} onClick={() => setAction([ActionTypes.SHOW_DEVICE, row])}>
              <ListItemText
                primary={`${row.fc_id} - ${row.name}`}
                secondary={`${t('Preset Dids')}: ${row.prepared}`}
              />
            </ListItemButton>
          ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearAction}>{t('Cancel')}</Button>
        </DialogActions>
      </Dialog>
    {Boolean(dialogContent) && (
      <Dialog open={true} onClose={clearAction}>
        <DialogContent>
          {dialogContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={clearAction}>{t('Cancel')}</Button>
          <Button onClick={confirmDialog}>{t('Confirm')}</Button>
        </DialogActions>
      </Dialog>
    )}
    </Page>
  );
};

export default GroupList;
